import { MainNavbarInterface } from '@type/header-types';
import { LogoBjLink } from '@components/layout/LogoBj/LogoBjLink';
import { Anchor } from '@components/common';
import {
  useCallback, useEffect, useState,
} from 'react';
import { clsx } from 'clsx';
import { useScrollDirection } from '@hooks/common/useScrollDirection';
import { LanguageList } from '@components/layout/LanguageSwitch/LanguageList';
import { MobileMenu } from '@components/layout/Header/MainNavbar/mobileMenu/MobileMenu';
import { SearchSection } from '@components/layout/Header/MainNavbar/search/SearchSection';
import { isHireRoute } from '@lib/functions/routes/isHireRoute';
import { isApplicantsFlowRoute } from '@lib/functions/routes/isApplicantsFlowRoute';
import { getNavbarTheme } from './config';
import { LoginButton, MainMenuLinks } from './links';
import { HeaderMissionLinks } from './HeaderMissionLinks';
import { ProfileSection } from './profile/ProfileSection';
import { MobileAccountMenu } from './profile/MobileAccountMenu';
import { MainNavbarPlaceholder } from './placeholders/MainNavbarPlaceholder';
import { FilterButton } from './search/FilterButton';
import { ContactPhoneChip } from './ContactPhoneChip';

/**
 * @description Main navbar component.
 * * **headerInfo** - authenticated user info
 * * **navbarTheme** - the navbar theme (transparent, default, etc.)
 * * **currentRoute** - the current route. Used to highlight the active link in the navbar
 * * **notPinned** - if the navbar is not pinned to the top of the page. When we scroll down,
 * the navbar will be pinned to the top of the page, so we can apply a different style to it.
 * * **locale** - the current locale, with default value 'ro'
 */
export const MainNavbar = (props: MainNavbarInterface) => {
  // Main component props with default values
  const {
    headerInfo = undefined,
    headerInfoLoading,
    headerInfoValidating,
    navbarTheme = 'default',
    currentRoute = '',
    notPinned = true,
    locale = 'ro',
  } = props;

  // Hide bottom nav on mobile while scrolling down
  // *****************************************************
  const isBottomNavHidden = useScrollDirection();

  // We make sure that we load the placeholder first.
  // *****************************************************
  const [initialStatus, setInitialStatus] = useState(true);
  useEffect(() => {
    setInitialStatus(false);
  }, []);


  // Conditionally show/hide components
  // *****************************************************
  const show = {
    placeholder: initialStatus || headerInfoLoading,
    revalidation: !initialStatus && !headerInfoLoading && headerInfoValidating,
    mainMenuLinks: !initialStatus && !headerInfoLoading,
    authUser: !initialStatus && !headerInfoLoading && headerInfo,
  };

  const classes = {
    container: 'py-2 lg:py-0',
    logoContainer: 'w-full',
    logo: 'mr-3 lg:mr-6',
  };

  // /stories + /profile have no job-search context, so suppress the header search there.
  const isStories = /^\/(?:[a-z]{2}\/)?stories(\/.*)?$/.test(currentRoute ?? '');
  const isProfile = /^\/(?:[a-z]{2}\/)?profile(\/.*)?$/.test(currentRoute ?? '');
  // Wallet + salaries pages have no job-search context, so suppress the header search
  // input and map button there too.
  const isWalletOrSalaries = /^\/(?:[a-z]{2}\/)?(?:wallet|salaries)(\/.*)?$/.test(currentRoute ?? '');
  // Messages page has no job-search context either — hide the search input and map button.
  const isMessages = /^\/(?:[a-z]{2}\/)?messages(\/.*)?$/.test(currentRoute ?? '');
  // Candidate "my jobs" pages (applications, saved jobs, cv-evaluations, interviews) render the
  // candidate sidebar with its own search context, so suppress the header search input and map
  // button there.
  const isMyJobsSidebarPage = /^\/(?:[a-z]{2}\/)?(?:applied-jobs|favorite-jobs|cv-evaluations|my-job-interviews)(\/.*)?$/.test(currentRoute ?? '');
  // /cv/search has its own bottom TalentSearchPrompt now — the header input would duplicate it.
  const isCvSearch = /^\/(?:[a-z]{2}\/)?cv\/search(\/.*)?$/.test(currentRoute ?? '');
  // Adding/editing a job has no search context — hide the candidate (talent) search input.
  const isJobAdd = /^\/(?:[a-z]{2}\/)?job-add(\/.*)?$/.test(currentRoute ?? '');
  const hideHeaderSearch = isStories || isProfile || isWalletOrSalaries || isMessages || isMyJobsSidebarPage || isCvSearch || isJobAdd;

  const renderSearchInput = useCallback(() => {
    if (hideHeaderSearch) return null;
    switch (true) {
      case isApplicantsFlowRoute(currentRoute):
        return null;
      // Company my-jobs search left the header too — it lives in the bottom
      // MyJobsSearchPrompt bar on the my-jobs pages now.
      case isHireRoute(currentRoute):
        // CV/talent search is desktop-only — hidden on mobile.
        return <div className="hidden lg:block"><SearchSection locale={locale} /></div>;
      // Candidate job-search input is retired from the header — shown via the prompt bar / pages instead.
      default:
        return null;
    }
  }, [currentRoute, locale, hideHeaderSearch]);

  // Render the navbar
  // *****************************************************
  return (
    <>
      <div className={getNavbarTheme(navbarTheme, notPinned)}>
        <div
          className={`container relative flex items-center justify-end lg:justify-normal ${classes.container}`}
        >

          <div
            className={`relative flex items-center justify-start lg:w-fit lg:justify-between ${classes.logoContainer}`}
          >
            <LogoBjLink
              headerInfo={headerInfo}
              locale={locale}
              className={`-mt-1 ${classes.logo}`}
            />

            {renderSearchInput()}

            {/* Filter button (job list only) first, then the contact number — the filters icon keeps precedence. */}
            <div className="ml-3 flex items-center gap-2">
              <FilterButton />
              <ContactPhoneChip />
            </div>

            {/* Mobile account — account icon opening the same dropdown as desktop ProfileSection. */}
            <div className="ml-auto flex shrink-0 items-center gap-3 lg:hidden">
              {headerInfo ? (
                <MobileAccountMenu headerInfo={headerInfo} />
              ) : show.mainMenuLinks ? (
                <LoginButton locale={locale} />
              ) : (
                <span className="block size-9 animate-pulse rounded-full bg-surface-200" />
              )}
            </div>
          </div>

          {/* AI — hardcoded external link to neogr.am, rendered as a button for visibility. Desktop-only;
              fills the gap between the phone chip and the menu, the 1:2 spacers put it one third of the way across. */}
          {show.mainMenuLinks && (
            <div className="hidden lg:flex lg:flex-1 lg:items-center">
              <div className="flex-1" />
              <Anchor
                href="https://www.neogr.am/"
                target="_blank"
                rel="noopener noreferrer"
                styling="solid"
                color="secondary"
                rounding="full"
                size="sm"
                className="shrink-0 !px-3 !py-1"
                data-test-id="navbar-ai"
              >
                AI
              </Anchor>
              <div className="flex-[2]" />
            </div>
          )}

          {/* Placeholder */}
          {show.placeholder && <MainNavbarPlaceholder />}

          {/* Validating new data (SWR has cache) */}
          {show.revalidation && <div className="absolute inset-0 z-10 flex animate-pulse bg-surface/50" />}

          {// Main menu links (both auth and not auth) and the login button for not auth users
            show.mainMenuLinks && (
              <nav className={clsx(
                'hidden',
                'lg:relative lg:z-auto lg:ml-auto lg:flex lg:h-auto lg:items-center lg:justify-end lg:border-0 lg:bg-transparent',
                'transition-transform duration-300 ease-in-out',
                isBottomNavHidden && 'translate-y-full lg:translate-y-0',
              )}
              >

                {/* Mission links — desktop counterpart of the mobile BottomNav */}
                <HeaderMissionLinks
                  locale={locale}
                  isAuthenticated={Boolean(headerInfo)}
                />

                {/* main menu links */}
                <MainMenuLinks
                  role={headerInfo?.role}
                  currentRoute={currentRoute}
                  locale={locale}
                />

                {!show.authUser && (
                  <>
                    <div className="relative ml-5 mt-1 hidden shrink-0 lg:block lg:px-1">
                      <LanguageList />
                    </div>

                    {/* Desktop Login button */}
                    <LoginButton className="hidden lg:flex" locale={locale} />
                  </>
                )}

                { // User is authenticated
                  show.authUser && (
                    <>
                      {/* Profile */}
                      <ProfileSection
                        headerInfo={headerInfo}
                        locale={locale}
                        className="ml-5 hidden shrink-0 lg:relative lg:ml-7 lg:block"
                      />
                    </>
                  )
                }

                {/* Mobile menu */}
                <MobileMenu
                  className="flex shrink-0 items-center justify-center lg:hidden"
                  headerInfo={headerInfo}
                  currentRoute={currentRoute}
                  locale={locale}
                />
              </nav>
            )
          }
        </div>
      </div>

      {/* This is used to calculate the height of the opened mobile menu */}
      <div id="menuOffsetSentinel" className="pointer-events-none absolute top-full h-0 w-full" />
    </>
  );
};
